import { useAuth0 } from "@auth0/auth0-react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  Stack,
} from "@mui/material";
import AppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIsMobile } from "../../hooks/isMobile/isMobile";
import { useLeague } from "../../hooks/useLeague/useLeague";
import CodeInput from "../codeinput/CodeInput";

interface JoinLeagueProps {
  open?: boolean;
  onClose?: () => void;
}

const JoinLeague = ({ open, onClose }: JoinLeagueProps) => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const { user, isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [inputValue, setInputValue] = useState("");
  const [showRedirecting, setShowRedirecting] = useState(false);
  const { joinLeague } = useLeague();
  // Helper to get ?code= from URL
  const getShareCodeFromQuery = () => {
    const params = new URLSearchParams(location.search);
    return params.get("code") || "";
  };

  // Helper to check if a string is a valid 6-char alphanumeric code
  const isValidCode = (code: string) => /^[A-Za-z0-9]{6}$/.test(code);

  // Try to read clipboard on dialog open or input focus
  useEffect(() => {
    if (typeof open === "boolean" && open && !inputValue && !codeFromQuery) {
      // Only try if dialog is open, no input yet, and no code in query
      if (navigator.clipboard && navigator.clipboard.readText) {
        navigator.clipboard
          .readText()
          .then((clip) => {
            if (isValidCode(clip)) {
              setInputValue(clip);
              handleJoinLeague(clip);
            }
          })
          .catch(() => {});
      }
    }
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    const code = getShareCodeFromQuery();
    // If there is a code, user is not authenticated, and not loading, trigger login
    if (code && !isAuthenticated && !isLoading) {
      setShowRedirecting(true);
      setTimeout(() => {
        loginWithRedirect({
          appState: { returnTo: location.pathname + location.search },
        });
      }, 700); // Show message for 700ms before redirect
      return;
    }
    if (code && !success && !error && isAuthenticated && user && !isLoading) {
      handleJoinLeague(code);
    }
    // eslint-disable-next-line
  }, [location.search, isAuthenticated, user, isLoading]);

  const handleJoinLeague = async (code: string) => {
    if (!isLoading && isAuthenticated && user) {
      try {
        const response = await joinLeague(code);
        if (response instanceof Error) {
          throw new Error(`Failed to join league: ${response.message}`);
        }

        setError(null); // Clear any previous errors
        setSuccess(true); // Show success alert

        // Wait 2 seconds before redirecting
        setTimeout(() => {
          if (onClose) onClose();
          setSuccess(false);
          // Redirect to the league page
          if (response) {
            navigate(`/leagues/${response.id}`);
          }
        }, 2000);
      } catch (error) {
        setError((error as Error).message);
        // Don't close the dialog on error
      }
    }
  };

  const codeFromQuery = getShareCodeFromQuery();

  const content = (
    <Stack sx={{ m: 2 }} spacing={2}>
      {showRedirecting && (
        <Card>
          <CardContent>
            <Typography variant="h6">Redirecting to login...</Typography>
          </CardContent>
        </Card>
      )}
      {!showRedirecting && error && (
        <Alert severity="error" onClose={() => setError(null)}>
          Failed to join league
        </Alert>
      )}
      {!showRedirecting && success && (
        <Alert severity="success">
          Successfully joined league! Redirecting...
        </Alert>
      )}
      {!showRedirecting && !codeFromQuery && (
        <CodeInput
          length={6}
          onComplete={handleJoinLeague}
          disabled={isLoading || success}
        />
      )}
      {!showRedirecting && codeFromQuery && !success && !error && (
        <Alert severity="info">Joining league with code: {codeFromQuery}</Alert>
      )}
    </Stack>
  );

  if (typeof open === "boolean") {
    return (
      <Dialog open={open} onClose={onClose} fullScreen={isMobile}>
        {isMobile ? (
          <AppBar
            position="static"
            color="default"
            elevation={0}
            sx={{ position: "relative" }}
          >
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={onClose}
                aria-label="close"
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" sx={{ flex: 1, ml: 1 }}>
                Join a Pickem League
              </Typography>
            </Toolbar>
          </AppBar>
        ) : (
          <>
            <DialogTitle sx={{ fontSize: 22, px: 3, py: 2 }}>
              Join a Pickem League
              <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
          </>
        )}
        <div style={{ padding: isMobile ? 12 : 24 }}>{content}</div>
      </Dialog>
    );
  }

  return (
    <div style={{ maxWidth: 400, margin: "40px auto" }}>
      <h2>Join a Pickem League</h2>
      {content}
    </div>
  );
};

export default JoinLeague;
